import { Button, Checkbox, Pagination } from "antd"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { FC } from "react"
import { ShiftRowActions } from "./ShiftSwapTable"
import ShiftSwapActivityRow from "./ShiftSwapActiveRow"
import { IShiftSwapsRes } from "../typings"
import { LoadingRegion } from "components/custom/Loader"
import { TableSkeleton } from "components/new-stuff/ui/Skeleton"

// ─── Types ───────────────────────────────────────────────────────────────────

interface ShiftSwapModalProps {
  isApprovingShiftSwap: boolean
  isRejectingShiftSwap: boolean
  selectedShiftRow: number[]
  shiftSwapDocs: IShiftSwapsRes | undefined
  isLoading: boolean
  emptyImage: string
  page: { currentPage: number; pageSize: number }
  onPageChange: (page: number, pageSize: number) => void
  onApprove: (ids: number[]) => void
  onReject: (ids: number[]) => void
  onToggleSelectAll: () => void
  onRowSelect: (id: number) => void
  formatTime: (time: string) => string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SHIFT_SWAP_HEADERS = [
  { label: "", width: "160.67px" },
  { label: "Name", width: "160.67px" },
  { label: "Shift plan", width: "160.67px" },
  { label: "Department", width: "160.67px" },
  { label: "Shift period", width: "160.67px" },
  { label: "Shift's date", width: "160.67px" },
  { label: "Reason", width: "160.67px" },
  { label: "Actions", width: "124px" },
]

const gridCols = SHIFT_SWAP_HEADERS.map((h) => h.width || "1fr").join(" ")

// ─── Sub Components ───────────────────────────────────────────────────────────

interface BulkActionsBarProps {
  isAllSelected: boolean
  selectedCount: number
  isApproving: boolean
  isRejecting: boolean
  onToggleSelectAll: () => void
  onBulkApprove: () => void
  onBulkReject: () => void
}

const BulkActionsBar: FC<BulkActionsBarProps> = ({
  isAllSelected,
  selectedCount,
  isApproving,
  isRejecting,
  onToggleSelectAll,
  onBulkApprove,
  onBulkReject,
}) => (
  <div className="flex items-center justify-between mb-4">
    <span className="flex items-center gap-2">
      <Checkbox
        onChange={onToggleSelectAll}
        checked={isAllSelected}
      />
      <p>
        Select all{" "}
        {selectedCount > 0 && (
          <span className="text-blue-500 font-semibold">({selectedCount})</span>
        )}
      </p>
    </span>
    <div className="flex items-center gap-3">
      <Button
        className="flex items-center h-10 gap-1 text-blue-500 border border-blue-500"
        onClick={onBulkApprove}
        disabled={!selectedCount || isApproving}
        loading={isApproving}
      >
        <CheckIcon className="w-4 h-4" />
        Bulk approve
      </Button>
      <Button
        className="flex items-center h-10 gap-1 text-red-500 border border-red-500"
        onClick={onBulkReject}
        disabled={!selectedCount || isRejecting}
        loading={isRejecting}
      >
        <XMarkIcon className="w-4 h-4" />
        Bulk reject
      </Button>
    </div>
  </div>
)

// ─── Table Header ─────────────────────────────────────────────────────────────

const TableHeader = () => (
  <ul
    className="grid text-slate-500 gap-3 text-sm items-center bg-[#F8FAFC] border-b border-gray-300 py-3 px-2 font-semibold"
    style={{ gridTemplateColumns: gridCols }}
  >
    {SHIFT_SWAP_HEADERS.map(({ label }, index) => (
      <li key={`${label}-${index}`}>{label}</li>
    ))}
  </ul>
)

// ─── Main Component ───────────────────────────────────────────────────────────

const ShiftSwapModal: FC<ShiftSwapModalProps> = ({
  isApprovingShiftSwap,
  isRejectingShiftSwap,
  selectedShiftRow,
  shiftSwapDocs,
  isLoading,
  emptyImage,
  page,
  onPageChange,
  onApprove,
  onReject,
  onToggleSelectAll,
  onRowSelect,
  formatTime,
}) => {
  const docs = shiftSwapDocs?.data?.docs ?? []
  const isAllSelected =
    docs.length > 0 && selectedShiftRow.length === docs.length

  return (
    <>
      <BulkActionsBar
        isAllSelected={isAllSelected}
        selectedCount={selectedShiftRow.length}
        isApproving={isApprovingShiftSwap}
        isRejecting={isRejectingShiftSwap}
        onToggleSelectAll={onToggleSelectAll}
        onBulkApprove={() => onApprove(selectedShiftRow)}
        onBulkReject={() => onReject(selectedShiftRow)}
      />
      <LoadingRegion
        spinning={isLoading}
        placeholder={<TableSkeleton columns={8} />}
      >
        <div className="overflow-x-auto w-full">
          {docs.length ? (
            <div className="border border-gray-300 rounded-lg overflow-hidden min-w-[1350px]">
              <TableHeader />
              <div className="bg-slate-100">
                {docs.map((item) => (
                  <div
                    key={item.id}
                    className="grid border-b border-b-gray-200 bg-white items-center gap-3 py-2"
                    style={{ gridTemplateColumns: gridCols }}
                  >
                    {/* 1. Checkbox column */}
                    <span className="flex justify-center items-center gap-2 px-2">
                      <Checkbox
                        onChange={() => onRowSelect(item.id)}
                        checked={selectedShiftRow.includes(item.id)}
                      />
                      <p className="text-sm text-slate-950">Select</p>
                    </span>

                    <div className="col-span-6 border-x border-x-gray-200">
                      <ShiftSwapActivityRow
                        shiftSwap={item}
                        requesterEmployee={item?.requester?.fullName}
                        targetEmployee={item?.targetEmployee?.fullName}
                        requesterShiftPlan={
                          item?.sourceShiftAssignment?.shiftPlan?.title
                        }
                        targetShiftPlan={
                          item?.targetShiftAssignment?.shiftPlan?.title
                        }
                        shiftPeriod={
                          item?.sourceShiftAssignment?.shiftPeriod?.title
                        }
                        requesterStartPeriod={formatTime(
                          item?.sourceShiftAssignment?.shiftPeriod?.startTime
                        )}
                        requesterEndPeriod={formatTime(
                          item?.sourceShiftAssignment?.shiftPeriod?.endTime
                        )}
                        targetStartPeriod={formatTime(
                          item?.targetShiftAssignment?.shiftPeriod?.startTime
                        )}
                        targetEndPeriod={formatTime(
                          item?.targetShiftAssignment?.shiftPeriod?.endTime
                        )}
                        requestershiftPeriod={
                          item?.sourceShiftAssignment?.shiftPeriod?.title
                        }
                        targeteshiftPeriod={
                          item?.targetShiftAssignment?.shiftPeriod?.title
                        }
                        requesterReason={item.requesterReason}
                        targetReason={item.targetEmployeeReason || ""}
                        requesterDepartment={
                          item?.requester?.department?.description
                        }
                        targetDepartment={
                          item?.targetEmployee?.department?.description
                        }
                      />
                    </div>

                    {/* 8. Actions column */}
                    <div className="flex justify-center items-center">
                      <ShiftRowActions
                        status={item?.approvalStatusId}
                        shiftSwap={item}
                        isLoading={isApprovingShiftSwap || isRejectingShiftSwap}
                        onApprove={() => onApprove([item.id])}
                        onReject={() => onReject([item.id])}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center py-10">
              <img
                src={emptyImage}
                className="object-contain bg-no-repeat"
                alt="No shift swaps"
              />
            </div>
          )}
        </div>
      </LoadingRegion>
      <div className="flex justify-start mt-6 mb-3">
        <Pagination
          current={page.currentPage}
          pageSize={page.pageSize}
          total={shiftSwapDocs?.data?.totalDocs}
          showSizeChanger
          pageSizeOptions={[5, 10, 20, 50]}
          onChange={onPageChange}
        />
      </div>
    </>
  )
}

export default ShiftSwapModal
